import { compact } from "lodash";
import moment from "moment";
import AppContext from "PFApp/app_context";
import ActivityPreviewLink from "PFApp/components/activity_commons/activity_preview_link";
import Gantt from "PFApp/components/gantt/gantt";
import Pill from "PFComponents/pill/pill";
import { RoleStatusBadge } from "PFCore/components/role/role_status_badge";
import { useRoleName } from "PFCore/helpers/activities";
import safePrune from "PFCore/helpers/safe_prune";
import { useReadableAvailability } from "PFCore/helpers/use_readable_availability";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import i18n from "PFCore/i18n";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";

import css from "./roles_gantt.modules.scss";
const GANTT_MATCH_MONTHS_LIMIT = 6; // for performance
const GANTT_MONTHS_LIMIT = 12; // for performance

const rowItemLabelComponent = (role) => (
  <ActivityPreviewLink
    activity={role}
    className={css.roleLabel}
    qaId={`WorkflowPageGanttRowActivityLink.${role.id}`}
  >
    {role.state === "draft" && (
      <Pill style={{ padding: "1px 8px 0px", alignItems: "baseline", marginRight: 10 }}>
        {i18n.t("draft")}
      </Pill>
    )}
    {safePrune(role.name, 60)}
  </ActivityPreviewLink>
);

const RolesGantt = ({ data }) => {
  const {
    store: { themeVars }
  } = useContext(AppContext);

  const { currentAccount } = useContext(AppContext).store;
  const { getReadableAvailability } = useReadableAvailability();

  let monthsLimit;
  try {
    monthsLimit = Math.min(currentAccount.availability.calculated_months_limit, GANTT_MONTHS_LIMIT);
  } catch {
    monthsLimit = GANTT_MONTHS_LIMIT;
  }

  let matchMonthsLimit;
  try {
    matchMonthsLimit = Math.min(currentAccount.availability.match_months_limit, GANTT_MATCH_MONTHS_LIMIT);
  } catch {
    matchMonthsLimit = GANTT_MATCH_MONTHS_LIMIT;
  }

  const getRoleName = useRoleName();
  const { formatISODate } = useDateFormatter();
  const startDate = useMemo(() => {
    const availabilityStartDates = compact(
      (data.entries || []).map(({ availability }) => {
        if (availability) {
          return moment.min(availability.ranges.map((range) => moment(range.start)));
        }
      })
    );
    const dateToFormat =
      moment.min(availabilityStartDates).valueOf() > moment().valueOf()
        ? moment.min(availabilityStartDates)
        : moment.max(moment(), moment.min(availabilityStartDates));

    return formatISODate(dateToFormat.utc().startOf("isoWeek").startOf("day"));
  }, [data.entries, formatISODate]);

  const getBarsFromItem = (role) => {
    const { availability } = role;

    if (!availability) {
      return null;
    }

    const periods = availability.ranges.map((range) => ({
      start: formatISODate(range.start),
      end: formatISODate(range.end)
    }));

    const forceBg =
      {
        partially_confirmed: themeVars["--WorkflowState-confirmed"],
        confirmed: themeVars["--WorkflowState-confirmed"],
        booked: themeVars["--WorkflowState-booked"],
        partially_booked: themeVars["--WorkflowState-booked"],
        filled: themeVars["--WorkflowState-filled"],
        partially_filled: themeVars["--WorkflowState-partially-filled"],
        shortlisting: themeVars["--WorkflowState-shortlisting"],
        auto_shortlisting: themeVars["--WorkflowState-auto-shortlisting"],
        manual_shortlisting: themeVars["--WorkflowState-manual-shortlisting"],
        not_filled: themeVars["--Palette-alert"],
        auto_filling: themeVars["--WorkflowState-auto-filling"]
      }[role.workflow_state] || themeVars["--Palette-neutral-mid"];

    return periods.map((period, index) => ({
      itemId: role.id,
      key: `${role.id}_${index}`,

      ...period,

      load: 100,
      partial: null,
      partialDayContent: null,

      forceBg,

      tooltipContent: (
        <div className={css.tooltip}>
          {role.engagement && <b className={css.tooltipHeader}>{role.engagement.name}</b>}
          <small>
            {getReadableAvailability({
              availability: { ...role.availability, ranges: [period] },
              options: { twoLines: true }
            })}
          </small>
          <RoleStatusBadge state={role.state} workflowState={role.workflow_state} />
        </div>
      )
    }));
  };

  return (
    <Gantt
      style={{ margin: "0 0 30px 0" }}
      monthsLimit={monthsLimit}
      matchMonthsLimit={matchMonthsLimit}
      getBarsFromItem={getBarsFromItem}
      rowItems={(data.entries || []).map(({ activity }) => ({ ...activity, name: getRoleName(activity) }))}
      startDate={startDate}
      changeStartDate={() => {}}
      availabilityInfo={{}}
      infoPanel={""}
      rowItemLabelComponent={rowItemLabelComponent}
    />
  );
};

RolesGantt.displayName = "role";

RolesGantt.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired
  }).isRequired,
  monthsLimit: PropTypes.number.isRequired,
  matchMonthsLimit: PropTypes.number.isRequired
};

export default RolesGantt;
